import React, { useState, useRef, useEffect } from 'react';

const CountryTypeahead = ({ countries, value, onSelect, inputId, width = '320px' }) => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = `${inputId}-listbox`;

  const text = editing ? query : (value || '');
  const q = query.trim().toLowerCase();
  const matches = editing && q
    ? countries.filter(c => c.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.toLowerCase().startsWith(q) ? 0 : 1;
        return aStarts - bStarts || a.localeCompare(b);
      })
    : countries;

  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIndex];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const commit = (country) => {
    onSelect(country);
    setEditing(false);
    setQuery('');
    setOpen(false);
    setActiveIndex(-1);
  };

  const startEditing = () => {
    if (!editing) { setEditing(true); setQuery(''); }
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { startEditing(); return; }
      setActiveIndex(i => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && matches.length > 0) {
        e.preventDefault();
        commit(matches[activeIndex >= 0 ? activeIndex : 0]);
      }
    } else if (e.key === 'Escape') {
      setEditing(false); setQuery(''); setOpen(false); setActiveIndex(-1);
    }
  };

  const handleBlur = () => {
    setEditing(false); setQuery(''); setOpen(false); setActiveIndex(-1);
  };

  return (
    <div style={{ position: 'relative', width }}>
      <div style={{ position: 'relative' }}>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
          autoComplete="off"
          placeholder={'Search for a country…'}
          value={text}
          onChange={(e) => { setEditing(true); setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
          onFocus={startEditing}
          onClick={startEditing}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '0.625rem 2.5rem 0.625rem 0.75rem',
            fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: 'white',
            color: '#1F2937'
          }}
        />
        {value && !editing ? (
          <button
            type="button"
            aria-label="Clear selected country"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { commit(''); if (inputRef.current) inputRef.current.focus(); }}
            style={{
              position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', color: '#6B7280', fontSize: '1.25rem', lineHeight: 1, cursor: 'pointer', padding: '0.25rem'
            }}
          >
            &times;
          </button>
        ) : (
          <span aria-hidden="true" style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: '0.875rem' }}>
            &#9662;
          </span>
        )}
      </div>
      {open && (
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="Countries"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
            margin: 0, padding: '0.25rem 0', listStyle: 'none', maxHeight: '280px', overflowY: 'auto',
            backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '6px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
          }}
        >
          {matches.length === 0 ? (
            <li style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: '#9CA3AF' }}>No countries match &ldquo;{query}&rdquo;</li>
          ) : matches.map((c, i) => {
            const isActive = i === activeIndex;
            const isSelected = c === value;
            return (
              <li
                key={c}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commit(c)}
                style={{
                  padding: '0.5rem 0.75rem', fontSize: '0.9375rem', cursor: 'pointer',
                  backgroundColor: isActive ? '#F3F4F6' : 'white',
                  color: '#1F2937', fontWeight: isSelected ? '600' : '400'
                }}
              >
                {c}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CountryTypeahead;
